import React, { useState, useEffect } from 'react';
import Box from '../uikit/box/Box';
import Text from '../uikit/text/Text';
import RatingDot from '../shared/rating/RatingDot';
import { getScopeSummary } from './IntelligenceService';

/**
 * Human vs AI averages and the gap between them (spec §8.1 summary view).
 *
 * Divergence is the product's signature metric: positive means respondents
 * rated the relationship more favourably than the transcript evidence did.
 *
 * Two things this has to be honest about, or the number misleads:
 *   - it is computed over MATCHED meetings only. A meeting with no AI composite
 *     leaves both averages, so `meeting_count` is usually lower than the total
 *     and is shown next to the figure rather than hidden.
 *   - with no human responses there is nothing to diverge from, so it renders
 *     as unavailable rather than as zero.
 */
const Stat = ({ label, value, rating }) => (
  <Box>
    <Text
      fontFamily="montserrat"
      fontSize={0}
      textTransform="uppercase"
      fontWeight="bold"
      color="secondarys.0"
      mb={1}
    >
      {label}
    </Text>
    <Box display="flex" alignItems="center" style={{ gap: '6px' }}>
      {rating != null && <RatingDot rating={rating} />}
      <Text fontFamily="montserrat" fontSize={4} fontWeight="bold">
        {value}
      </Text>
    </Box>
  </Box>
);

const DivergenceSummary = ({ scope, scopeId }) => {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (!scopeId) return;
    getScopeSummary(scope, scopeId)
      .then(setSummary)
      .catch(() => setSummary(null));
  }, [scope, scopeId]);

  if (!summary) return null;

  const { human_average, ai_composite_average, divergence, meeting_count, respondent_count } = summary;

  // Nothing scored, or nobody surveyed: say so plainly instead of showing 0.
  if (divergence === null || divergence === undefined) {
    return (
      <Box p={3}>
        <Text fontFamily="montserrat" fontSize={1} color="grays.2">
          {meeting_count === 0
            ? 'No meetings have both an AI rating and attendee feedback yet, so there is nothing to compare.'
            : 'Not enough data to compare attendee and AI ratings.'}
        </Text>
      </Box>
    );
  }

  const sign = divergence > 0 ? '+' : '';
  const reading =
    divergence > 0
      ? 'Attendees rated these meetings more favourably than the transcripts did.'
      : divergence < 0
        ? 'The transcripts rated these meetings more favourably than attendees did.'
        : 'Attendee and AI ratings agree.';

  return (
    <Box p={3}>
      <Box display="flex" flexWrap="wrap" style={{ gap: '32px' }}>
        <Stat label="Attendees' rating" value={human_average} rating={human_average} />
        <Stat label="AI rating" value={ai_composite_average} rating={ai_composite_average} />
        <Stat label="Divergence" value={`${sign}${divergence}`} />
      </Box>
      <Text fontFamily="montserrat" fontSize={1} mt={3}>
        {reading}
      </Text>
      <Text fontFamily="montserrat" fontSize={0} color="grays.2" mt={1}>
        {/* Sample size next to the number, so a divergence drawn from two
            meetings is not read with the confidence of one drawn from twenty. */}
        Across {meeting_count} meeting{meeting_count === 1 ? '' : 's'} with both ratings
        {respondent_count ? `, ${respondent_count} respondent${respondent_count === 1 ? '' : 's'}` : ''}.
      </Text>
    </Box>
  );
};

export default DivergenceSummary;
